/**
 * Value mapper for the unilever italy dashboard.
 * Converts the data retrieved from the server to data used by the widgets.
 */
import {ClosedOpenedAlarmByDay} from "../widgets/unilever/AlarmsManagementWidget";
import {AssetBaseCardsData, EquipmentByManufacturer} from "../widgets/unilever/AssetBaseDashboardWidget";
import {EquipmentByCommercialNode} from "../widgets/unilever/EquipmentByLevelWidget";
import {EquipmentByModel} from "../widgets/unilever/EquipmentByModelWidget";
import {EquipmentPlacementCardsData} from "../widgets/unilever/EquipmentPlacementsWidget";
import {CardsData} from "../widgets/unilever/NotAtManufacturersWidget";
import {AlarmByCommercialNode} from "../widgets/unilever/OpenAlarmsByLevelWidget";
import {AlarmByEquipmentType, AlarmByType} from "../widgets/unilever/OpenAlarmsWidget";
import {DashboardRequestsData} from "./DashboardData";
import JSONEquipmentCount = DashboardRequestsData.JSONEquipmentCount;

const MovementTypeUUID = {
    NEW_TO_MARKET: "b904cfde-2858-11e9-8cac-b2a6d5960774",
    MARKET_TO_NEW: "a8372c42-2858-11e9-8cac-b2a6d5960774",
    MARKET_TO_UNKNOWN: "912d838e-2858-11e9-8cac-b2a6d5960774",
    UNKNOWN_TO_MARKET: "70cbfe40-2858-11e9-8cac-b2a6d5960774",
    INTERNAL: "55cb8fb2-39e1-11e9-94d1-b2a6d5960774",
};

const PlacementTypeUUID = {
    MARKET: "484e4004-247a-11e9-ba62-941882820f48",
    STORAGE: "3cd3350e-247a-11e9-ba62-941882820f48",
    UNKNOWN: "4b83954e-247a-11e9-ba62-941882820f48",
    MANUFACTURER: "2f7629f2-247a-11e9-ba62-941882820f48",
};

export class JSONMapper {

    public static jsonAlarmsByDateMapper(
        openAlarms: DashboardRequestsData.JSONAlarmsByDateCount[],
        closedAlarms: DashboardRequestsData.JSONAlarmsByDateCount[]): ClosedOpenedAlarmByDay[] {
        return openAlarms
            .map((row) => {
                    function getClosedAlarmsCount(
                        alarmsCountList: DashboardRequestsData.JSONAlarmsByDateCount[],
                        date: string,
                    ) {
                        const r = alarmsCountList.filter((element) => element.date === date);
                        if (r.length > 0) {
                            return r[0].count;
                        }
                        return 0;
                    }

                    return {
                        dayNumber: row.dateString,
                        openedAlarms: row.count,
                        closedAlarms: getClosedAlarmsCount(closedAlarms, row.date),
                    };
                },
            );
    }

    public static jsonEquipmentByModelMapper(
        rows: DashboardRequestsData.JSONEquipmentByModelCount[],
    ): EquipmentByModel[] {
        return rows
            .sort((row1, row2) => row1.count - row2.count)
            .map((row) => {
                    return {
                        modelGuid: row.modelGuid,
                        modelName: row.modelName,
                        modelId: row.modelId,
                        brandGuid: row.brandGuid,
                        typeGuid: row.typeGuid,
                        typeColor: row.typeColor,
                        equipmentCount: row.count,
                    };
                },
            );
    }

    public static jsonEquipmentMovements(
        rows: DashboardRequestsData.JSONMovementCount[],
    ): EquipmentPlacementCardsData {

        function getPlacementCountByType(movementTypeUUID: string): number {
            const row = rows.filter((element) => element.movementTypeGuid === movementTypeUUID);
            if (row.length > 0) {
                return row[0].count;
            }
            return 0;
        }

        return {
            internalMovementsCount: getPlacementCountByType(MovementTypeUUID.INTERNAL),
            recoveredEquipmentCount: getPlacementCountByType(MovementTypeUUID.UNKNOWN_TO_MARKET),
            unreachableEquipmentCount: getPlacementCountByType(MovementTypeUUID.MARKET_TO_UNKNOWN),
            newEquipmentCount: getPlacementCountByType(MovementTypeUUID.NEW_TO_MARKET),
        };
    }

    public static jsonAlarmsCountByCommercialHierarchyMapper(
        rows: DashboardRequestsData.JSONAlarmsCount[],
    ): AlarmByCommercialNode[] {
        return rows
            .map((row) => {
                    return {
                        levelName: row.keyName,
                        levelKey: row.key,
                        alarmsByType: row.alarmsCountList.map(
                            (el) => {
                                return {
                                    typeGuid: el.key,
                                    typeName: el.keyName,
                                    typeColor: el.keyColor,
                                    alarmsCount: el.count,
                                    severity: el.severity,
                                };
                            },
                        ),
                    };
                },
            );
    }

    public static jsonAlarmsCountByEquipmentFamilyMapper(
        rows: DashboardRequestsData.JSONAlarmsCountWithEquipmentCount[],
    ): AlarmByEquipmentType[] {
        return rows
            .map((row) => {
                    return {
                        familyName: row.keyName,
                        familyIcon: row.image,
                        familyGuid: row.key,
                        alarmsCount: row.count,
                        equipmentWithAlarm: row.equipmentWithAlarmsCount,
                        totalEquipment: row.totalEquipment,
                        alarmOpenAgeAvg: row.alarmOpenAgeAvg,
                        alarmsByType: row.alarmsCountList.map(
                            (el) => {
                                return {
                                    typeGuid: el.key,
                                    typeName: el.keyName,
                                    typeColor: el.keyColor,
                                    alarmsCount: el.count,
                                    severity: el.severity,
                                };
                            },
                        ),
                    };
                },
            );
    }

    public static jsonEquipmentByManufacturerMapper(
        rows: DashboardRequestsData.JSONEquipmentCount[],
    ): EquipmentByManufacturer[] {
        return rows
            .map((row) => {
                    return {
                        manufacturerName: row.keyName,
                        equipmentByType: row.equipmentCountList.map(
                            (el) => {
                                return {
                                    typeGuid: el.key,
                                    typeName: el.keyName,
                                    typeLogo: el.icon,
                                    typeColor: el.keyColor,
                                    equipmentCount: el.count,
                                };
                            },
                        ),
                    };
                },
            );
    }

    public static jsonEquipmentCountByCommercialHierarchyMapper(
        rows: DashboardRequestsData.JSONEquipmentCount[],
    ): EquipmentByCommercialNode[] {
        return rows
            .map((row) => {
                    return {
                        levelName: row.keyName,
                        levelKey: row.key,
                        equipmentByPlacement: row.equipmentCountList.map(
                            (el) => {
                                return {
                                    placementGuid: el.key,
                                    placementName: el.keyName,
                                    equipmentCount: el.count,
                                };
                            },
                        ),
                    };
                },
            );
    }

    public static jsonAlarmsByTypeMapper(rows: DashboardRequestsData.JSONAlarmsCount[]): AlarmByType[] {
        return rows
            .sort((row1, row2) => row1.count - row2.count)
            .map((row) => {
                    return {
                        typeGuid: row.key,
                        typeName: row.keyName,
                        typeColor: row.keyColor,
                        alarmsCount: row.count,
                        severity: row.severity,
                    };
                },
            );
    }

    public static jsonEquipmentByTypeMapper(
        rows: DashboardRequestsData.JSONEquipmentCount[],
    ): DashboardRequestsData.EquipmentByType[] {
        return rows
            .sort((row1, row2) => row1.count - row2.count)
            .map((row) => {
                    return {
                        typeGuid: row.key,
                        typeName: row.keyName,
                        typeLogo: row.icon,
                        typeColor: row.keyColor,
                        equipmentCount: row.count,
                    };
                },
            );
    }

    public static getPlacementCount(rows: DashboardRequestsData.JSONEquipmentCount[], placementTypeUUID) {
        const row = rows.filter(
            (element) => element.key === placementTypeUUID,
        );
        if (row.length > 0) {
            return row[0].count;
        }
        return 0;
    }

    public static jsonEquipmentDealersCardsDataMapper(rows: DashboardRequestsData.JSONEquipmentCount[]): CardsData {
        const newEquipmentCount = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.STORAGE);
        const onMarketEquipmentCount = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.MARKET);
        const equipmentInUnknownPosCount = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.UNKNOWN);
        return {
            equipmentTotal: newEquipmentCount + onMarketEquipmentCount + equipmentInUnknownPosCount,
            newEquipmentCount,
            onMarketEquipmentCount,
            equipmentInUnknownPosCount,
        };
    }

    public static jsonEquipmentAssetBaseCardsDataMapper(
        rows: DashboardRequestsData.JSONEquipmentCount[],
    ): AssetBaseCardsData {
        const newEquipment = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.STORAGE);
        const onMarketEquipment = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.MARKET);
        const equipmentInUnknownPos = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.UNKNOWN);
        const onManufacturerEquipment = JSONMapper.getPlacementCount(rows, PlacementTypeUUID.MANUFACTURER);
        const total = newEquipment + onMarketEquipment + equipmentInUnknownPos + onManufacturerEquipment;
        return {
            equipmentTotal: total,
            equipmentOfManufacturers: onManufacturerEquipment,
        };
    }

    public static getOnMarketCount(list: JSONEquipmentCount[]) {
        return JSONMapper.getPlacementCount(list, PlacementTypeUUID.MARKET);
    }
}
